import { studioBindingTreeId } from 'src/components/star-grid/utils/studioBindings';
import { preserveStudioMemberOrder } from 'src/components/star-grid/utils/studioMemberSelection';
import { useCallback, useMemo, useState } from 'react';
import type { StudioBinding, StudioField } from 'src/components/star-grid/reportStudio.types';
import { loadStudioHierarchyTree } from 'src/components/star-grid/services/studioHierarchyService';
import { useReportParameter } from 'src/components/star-grid/hooks/useReportParameter';
import { selectedMemberPositions } from 'src/components/star-grid/utils/reportParameterHierarchy';
export const useStudioField = (
  field: StudioField,
  binding: StudioBinding,
  onChange: (field: StudioField) => void,
  singleMember = false,
  defaultOpen = false
) => {
  const [detailsOpen, setDetailsOpen] = useState(defaultOpen);
  const multiple = ['all', 'members'].includes(field.selection.kind) && !singleMember;
  const usesMembers = ['all', 'members', 'children', 'descendants'].includes(field.selection.kind);
  const parameter = useMemo(
    () => ({
      key: field.id,
      label: binding.label,
      role: 'fact_filter' as const,
      required: false,
      multiple: true,
      default: [],
      hierarchy_id: studioBindingTreeId(binding)
    }),
    [field.id, binding]
  );
  const loadHierarchy = useCallback((_id: string, signal: AbortSignal) => loadStudioHierarchyTree(binding, signal), [binding]);
  const choices = useReportParameter(parameter, field.selection.members, detailsOpen && usesMembers, loadHierarchy);
  const initialChecked = useMemo(
    () => selectedMemberPositions(choices.tree?.members ?? [], field.selection.members, multiple),
    [choices.tree, field.selection.members, multiple]
  );
  const confirm = (members: string[]) => {
    const kind = ['all', 'members'].includes(field.selection.kind) ? (members.length ? 'members' : 'all') : field.selection.kind;
    onChange({ ...field, selection: { ...field.selection, kind, members: preserveStudioMemberOrder(field.selection.members, members) } });
    setDetailsOpen(false);
  };
  return { detailsOpen, setDetailsOpen, multiple, usesMembers, choices, initialChecked, confirm };
};
